/** Maps vehicle state → feed name (or null for "no vehicles expected"). */
export type StateToFeedMap = Record<string, string | null>

/** Maps condition → (state → feed). */
export type FeedMapping = Record<string, StateToFeedMap>

export const VALID_CONDITIONS = ['new', 'used'] as const
export type FeedMappingCondition = (typeof VALID_CONDITIONS)[number]

export const VEHICLE_STATES = [
  'Dealer Ordered',
  'In Production',
  'In Transit',
  'Ready for Sale',
  'In-Service FCTP',
  'Ready FCTP',
  'Reserved',
  'Sold',
  '', // routing catch-all (canonical inventory state is UNKNOWN)
] as const

export const VEHICLE_STATE_LABELS: Record<string, string> = {
  'Dealer Ordered': 'Dealer Ordered',
  'In Production': 'In Production',
  'In Transit': 'In Transit',
  'Ready for Sale': 'Ready for Sale',
  'In-Service FCTP': 'In-Service Courtesy Vehicle',
  'Ready FCTP': 'Ready Courtesy Vehicle',
  'Reserved': 'Reserved',
  'Sold': 'Sold',
  '': 'Other / Unknown',
}

// ---------------------------------------------------------------------------
// Override / attribute-based exceptions
// ---------------------------------------------------------------------------

export const OVERRIDE_OPERATORS = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'missing',
  'not_missing',
] as const

export type OverrideOperator = (typeof OVERRIDE_OPERATORS)[number]

export const OVERRIDE_OPERATOR_LABELS: Record<OverrideOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  missing: 'is missing',
  not_missing: 'is present',
}

export interface FeedMappingOverrideFilter {
  field: string
  operator: OverrideOperator
  /** Ignored for 'missing' / 'not_missing' operators. */
  value: string
}

export interface FeedMappingOverride {
  condition: FeedMappingCondition
  state: string
  filter: FeedMappingOverrideFilter
  feed: string
}

// ---------------------------------------------------------------------------
// Fallback sources — per (condition, state) secondary feed checked before blocking
// ---------------------------------------------------------------------------

/**
 * When a vehicle is not in the primary designated feed for (condition, state),
 * also check these fallback feeds before blocking.
 * Vehicle is only blocked if absent from BOTH primary AND all fallback feeds.
 */
export interface FeedMappingFallback {
  condition: FeedMappingCondition
  state: string
  feed: string
}

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

export interface SaveFeedMappingResponse {
  /** The matrix actually persisted after backend defaults and validation. */
  effectiveFeedMapping?: FeedMapping
  derivedPrimarySources: string[]
  generatedRuleCount: number
  /**
   * Generated blocking rules are staged, not published. Saving Feed Source Config
   * never mutates published listing rules; rules are (re)generated during Publish Changes.
   */
  rulesStagedForPublish: boolean
  matrixSavedAsDraft: boolean
  warnings: string[]
}

export interface GetFeedMappingResponse {
  feedMapping: FeedMapping | null
  overrides: FeedMappingOverride[]
  fallbacks: FeedMappingFallback[]
  feedMappingVersion: number | null
  source: 'draft' | 'published' | 'none'
  derivedPrimarySources?: string[]
}

// ---------------------------------------------------------------------------
// Unpublished Changes — server-computed FSC draft diff
// ---------------------------------------------------------------------------

export interface FeedMappingMatrixAssignmentChange {
  condition: string
  state: string
  fromFeed: string | null
  toFeed: string | null
}

export interface FeedMappingOverrideItemChange {
  kind: 'added' | 'removed' | 'modified'
  label: string
}

export interface FeedMappingFallbackItemChange {
  kind: 'added' | 'removed' | 'modified'
  label: string
}

export interface FeedMappingDraftDiff {
  matrix: {
    total: number
    changes: FeedMappingMatrixAssignmentChange[]
  }
  overrides: {
    added: number
    removed: number
    modified: number
    preview: FeedMappingOverrideItemChange[]
  }
  fallbacks: {
    added: number
    removed: number
    modified: number
    preview: FeedMappingFallbackItemChange[]
  }
}

export interface FeedMappingDraftSummary {
  changed: boolean
  savedAt?: string
  savedBy?: string
  diff?: FeedMappingDraftDiff
}
